// JobClose AI · Pass 4C · Team Members
//
// Minimum UI to operate the Pass 4B backend Invitations + Membership
// Lifecycle. Owner-only mutation controls; member sees the roster but
// no controls. No Stripe touch, no destructive company/workspace
// controls, no ownership-transfer UI, no account deletion.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, UserPlus, X, Users, Mail } from "lucide-react";

const humanizeError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail !== "string") return "Something went wrong. Please try again.";
  switch (detail) {
    case "already_member":
      return "This person already has access to your company.";
    case "user_already_in_organization":
      return "This user is currently connected to another JobClose company.";
    case "owner_required":
      return "You don't have permission to manage team members.";
    case "no_active_organization":
      return "Your company workspace is not fully set up yet.";
    case "invitation_not_found":
      return "That invitation is no longer available.";
    case "invitation_expired":
      return "That invitation has expired.";
    case "sole_owner_cannot_be_removed":
      return "You can't remove the company owner.";
    case "membership_removed":
    case "membership_revoked":
      return "That membership is no longer active.";
    default:
      return detail;
  }
};

const StatusPill = ({ status }) => {
  const map = {
    active: "bg-[var(--jc-teal)]/15 text-[var(--jc-teal)]",
    pending: "bg-[var(--jc-blue)]/15 text-[var(--jc-blue)]",
    removed: "bg-[var(--jc-border)] text-[var(--jc-text-soft)]",
  };
  const label = {
    active: "Active",
    pending: "Pending",
    removed: "Removed",
  }[status] || status;
  return (
    <span
      className={
        "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide " +
        (map[status] || "bg-[var(--jc-border)] text-[var(--jc-text-soft)]")
      }
      data-testid={`status-pill-${status}`}
    >
      {label}
    </span>
  );
};

const RoleLabel = ({ role }) => (
  <span className="text-xs text-[var(--jc-text-soft)] uppercase tracking-wide font-semibold">
    {role === "owner" ? "Owner" : "Member"}
  </span>
);

const TeamMembers = () => {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // membership row
  const [removeBusy, setRemoveBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState("");

  // Owner is the caller whose active membership in this org is role=owner.
  const isOwner = useMemo(() => {
    if (!user) return false;
    const row = memberships.find((m) => m.user_id === user.id && m.status === "active");
    return row?.role === "owner";
  }, [memberships, user]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mems, invs, seatsResp] = await Promise.all([
        api.get("/memberships"),
        api.get("/invitations").catch(() => ({ data: [] })), // members can't list
        api.get("/organizations/seats").catch(() => ({ data: null })),
      ]);
      setMemberships(mems.data || []);
      // Only show pending; accepted/revoked history is not surfaced.
      setInvitations((invs.data || []).filter((i) => i.status === "pending"));
      setSeats(seatsResp.data || null);
    } catch (err) {
      // If the API returns 403 membership_inactive the axios interceptor
      // will already have bounced the browser to /no-access. Any other
      // failure surfaces once, non-fatally.
      if (err?.response?.data?.detail !== "membership_inactive") {
        toast.error(humanizeError(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeMemberships = memberships.filter((m) => m.status === "active");

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setInviteBusy(true);
    try {
      await api.post("/invitations", { email });
      toast.success(`Invitation sent to ${email}.`);
      setInviteEmail("");
      setInviteOpen(false);
      await refresh();
    } catch (err) {
      toast.error(humanizeError(err));
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async (inv) => {
    setRevokeBusy(inv.id);
    try {
      await api.post(`/invitations/${inv.id}/revoke`);
      toast.success("Invitation revoked.");
      await refresh();
    } catch (err) {
      toast.error(humanizeError(err));
    } finally {
      setRevokeBusy("");
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      await api.post(`/memberships/${removeTarget.id}/remove`);
      toast.success(`${removeTarget.full_name || removeTarget.email || "Member"} removed from your company.`);
      setRemoveTarget(null);
      await refresh();
    } catch (err) {
      toast.error(humanizeError(err));
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <DashboardLayout
      title="Team Members"
      subtitle="Manage who has access to your company workspace on JobClose."
    >
      <div className="max-w-4xl space-y-6" data-testid="team-members-page">

        {/* Team-size summary + Invite CTA */}
        <div className="jc-card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="team-summary-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
              <Users size={18} />
            </div>
            <div className="min-w-0">
              <div className="overline">Company access</div>
              <div className="text-sm text-[var(--jc-text)] font-semibold">
                {loading ? (
                  <span className="text-[var(--jc-text-soft)]">Loading…</span>
                ) : (
                  <span data-testid="team-summary-line">
                    Active users:{" "}
                    <span data-testid="team-active-count">
                      {seats?.active_members ?? activeMemberships.length}
                    </span>
                    {seats && seats.billable_additional_users > 0 ? (
                      <span className="text-[var(--jc-text-soft)] font-normal">
                        {"  ·  "}
                        <span data-testid="team-additional-count">{seats.billable_additional_users}</span> additional user
                        {seats.billable_additional_users === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="text-[var(--jc-text-soft)] font-normal">
                        {"  ·  "}
                        <span data-testid="team-included-count">{seats?.included_users ?? 2}</span> included
                      </span>
                    )}
                  </span>
                )}
              </div>
              <p className="text-xs text-[var(--jc-text-soft)] mt-1">
                Company records stay with your company. Team members can be added or removed at any time without deleting their JobClose identity.
              </p>
            </div>
          </div>
          {isOwner && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              data-testid="invite-member-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary self-start sm:self-auto"
            >
              <UserPlus size={14} /> Invite Team Member
            </button>
          )}
        </div>

        {/* Active members */}
        <div className="jc-card p-5" data-testid="active-members-card">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Active members</div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--jc-text-soft)]" data-testid="active-members-loading">
              <Loader2 className="animate-spin" size={14} /> Loading…
            </div>
          ) : activeMemberships.length === 0 ? (
            <div className="text-sm text-[var(--jc-text-soft)]" data-testid="active-members-empty">
              No active members yet.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--jc-border)]" data-testid="active-members-list">
              {activeMemberships.map((m) => {
                const isSelf = user && m.user_id === user.id;
                const isTargetOwner = m.role === "owner";
                const canRemove = isOwner && !isSelf && !isTargetOwner;
                return (
                  <li
                    key={m.id}
                    className="py-3 flex items-center gap-3"
                    data-testid={`member-row-${m.user_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[var(--jc-text)] truncate" data-testid={`member-name-${m.user_id}`}>
                        {m.full_name || m.email || "—"}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--jc-text-soft)]" data-testid={`member-self-tag-${m.user_id}`}>You</span>}
                      </div>
                      <div className="text-xs text-[var(--jc-text-soft)] truncate">
                        {m.email || "—"}
                      </div>
                    </div>
                    <div className="hidden sm:block"><RoleLabel role={m.role} /></div>
                    <StatusPill status="active" />
                    {canRemove ? (
                      <button
                        type="button"
                        onClick={() => setRemoveTarget(m)}
                        data-testid={`remove-member-btn-${m.user_id}`}
                        className="text-xs font-semibold text-[var(--jc-red, #b91c1c)] hover:underline shrink-0"
                      >
                        Remove
                      </button>
                    ) : (
                      // No Remove control for the caller themselves, for owners,
                      // or for members (who never see controls).
                      <span className="w-[60px] shrink-0" aria-hidden="true" />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pending invitations (owner-only) */}
        {isOwner && (
          <div className="jc-card p-5" data-testid="pending-invites-card">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Pending invitations</div>
            </div>
            {invitations.length === 0 ? (
              <div className="text-sm text-[var(--jc-text-soft)]" data-testid="pending-invites-empty">
                No pending invitations.
              </div>
            ) : (
              <ul className="divide-y divide-[var(--jc-border)]" data-testid="pending-invites-list">
                {invitations.map((inv) => (
                  <li
                    key={inv.id}
                    className="py-3 flex items-center gap-3"
                    data-testid={`invite-row-${inv.id}`}
                  >
                    <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
                      <Mail size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[var(--jc-text)] truncate" data-testid={`invite-email-${inv.id}`}>
                        {inv.invitee_email}
                      </div>
                      <div className="text-xs text-[var(--jc-text-soft)]">
                        Invited — awaiting response
                      </div>
                    </div>
                    <StatusPill status="pending" />
                    <button
                      type="button"
                      onClick={() => revokeInvite(inv)}
                      disabled={revokeBusy === inv.id}
                      data-testid={`revoke-invite-btn-${inv.id}`}
                      className="text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] hover:underline disabled:opacity-50 shrink-0 inline-flex items-center gap-1"
                    >
                      {revokeBusy === inv.id ? (
                        <><Loader2 className="animate-spin" size={12} /> Revoking…</>
                      ) : (
                        <><X size={12} /> Revoke</>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(v) => { if (!inviteBusy) setInviteOpen(v); }}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
            <DialogDescription>
              Enter the work email of the person you want to give access to this JobClose company workspace.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-3">
            <div>
              <label htmlFor="invite-email" className="text-xs font-semibold text-[var(--jc-text-soft)] uppercase tracking-wide">
                Work email
              </label>
              <input
                id="invite-email"
                type="email"
                autoFocus
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@example.com"
                data-testid="invite-email-input"
                className="mt-1 w-full px-3 py-2 rounded-md border border-[var(--jc-border)] focus:outline-none focus:border-[var(--jc-blue)] text-sm"
                disabled={inviteBusy}
                required
              />
            </div>
            <p className="text-[11px] text-[var(--jc-text-soft)]">
              A one-time invitation link will be sent. The link expires in 14 days.
            </p>
            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                disabled={inviteBusy}
                data-testid="invite-cancel-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteBusy || !inviteEmail.trim()}
                data-testid="invite-submit-btn"
                className="px-3 py-2 rounded-md text-xs font-semibold jc-btn-primary disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {inviteBusy ? (
                  <><Loader2 className="animate-spin" size={12} /> Sending…</>
                ) : (
                  <>Send invitation</>
                )}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(v) => { if (!v && !removeBusy) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid="remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removeTarget?.full_name || removeTarget?.email || "this member"} from your company?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.full_name?.split(" ")?.[0] || "This member"} will immediately lose access to this JobClose workspace. Your company will keep all customers, projects, estimates, invoices, documents, and other work they created.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={removeBusy}
              data-testid="remove-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              disabled={removeBusy}
              data-testid="remove-confirm-btn"
              className="bg-[var(--jc-red,#b91c1c)] hover:bg-[var(--jc-red,#7f1d1d)] text-white"
            >
              {removeBusy ? (
                <><Loader2 className="animate-spin mr-1.5" size={12} /> Removing…</>
              ) : (
                <>Remove Member</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default TeamMembers;
